// 品番原価の原価パターン照会と通貨単位の同期をまとめる。
// 本文は HinbanMaster.js から移設したもの(純粋な判定部分は shared/entry/genkaPatternResolver 側)。

export function createGenkaPatternLoader(deps) {
    const {
        DicVal,
        HINBAN_GENKA_PANEL_TYPE,
        applyForcedGenkaJpyIfNeeded,
        buildGenkaPatternRequest,
        deriveGenkaPatternFromEntry,
        dispatch,
        edittingcode,
        entrydata,
        entrydataRef,
        entryrerender,
        fnc,
        genkaBrandKbnColumnIdCandidates,
        genkaCurrencyOptionsFromLayout,
        genkaHeaderCurrencyColumnIdCandidates,
        genkaKaigaiKbnColumnIdCandidates,
        genkaPatternLabelByNo,
        genkaPatternRequestSeqRef,
        genkaPatternUrl,
        genkaShohinKbnColumnIdCandidates,
        genkaSupplierCurrencyColumnIdCandidates,
        genkaYushoMushoKbnColumnIdCandidates,
        isEmpty,
        isGenkaCurrencyEditableByLayout,
        normalizeGenkaPatternNo,
        recalcHinbanGenkaGridAndSummary,
        rerender,
        resolveGenkaCurrencyFromEntry,
        resolveKakousijiGridPanelByType,
        setGenkaCurrency,
        setGenkaCurrencyNo,
        setGenkaCurrencyOptions,
        setGenkaCurrencyToEntry,
        setGenkaPattern,
        setGenkaPatternNo,
        setGenkaRateToEntry,
        shouldForceGenkaCurrencyJpy
    } = deps;

    function isHinbanGenkaPanel(panelInfo) {
        if (isEmpty(panelInfo)) return false;
        return String(DicVal(panelInfo, 'type', '')).trim() === HINBAN_GENKA_PANEL_TYPE;
    }
    function applyGenkaPatternResult(source) {
        const patternNo = normalizeGenkaPatternNo(DicVal(source, 'patternNo', DicVal(source, 'PatternNo', 0)));
        const patternName = String(DicVal(source, 'patternName', DicVal(source, 'PatternName', '')) ?? '').trim();
        setGenkaPatternNo(patternNo);
        setGenkaPattern(patternName !== '' ? patternName : genkaPatternLabelByNo(patternNo));
    }
    function applyGenkaPatternFallback(entryObj) {
        const fallbackNo = deriveGenkaPatternFromEntry(entryObj);
        setGenkaPatternNo(0);
        setGenkaPattern(genkaPatternLabelByNo(fallbackNo));
    }
    function loadHinbanGenkaPattern(code, entryObj = entrydataRef.current) {
        const request = buildGenkaPatternRequest(code, entryObj);
        const hasSourceValue = [request.code, request.oppiCd, request.kaigaiKbn, request.kakouSakiKbn, request.kakouSakiCd, request.kakouSakiSubCd, request.tsukaTani]
            .some((value) => String(value ?? '').trim() !== '');
        if (!hasSourceValue) {
            applyGenkaPatternFallback(entryObj);
            return;
        }
        const requestSeq = genkaPatternRequestSeqRef.current + 1;
        genkaPatternRequestSeqRef.current = requestSeq;
        fnc.current.postaxios(genkaPatternUrl, request, {
            onSuccess: function (data) {
                try {
                    if (genkaPatternRequestSeqRef.current !== requestSeq) return;
                    applyGenkaPatternResult(data);
                } finally {
                    fnc.current.wait(false);
                }
            },
            onError: function () {
                try {
                    if (genkaPatternRequestSeqRef.current !== requestSeq) return;
                    applyGenkaPatternFallback(entryObj);
                } finally {
                    fnc.current.wait(false);
                }
            }
        });
    }
    function syncHinbanGenkaCurrencyState(entryObj = entrydataRef.current, applyEntry = false) {
        if (isEmpty(entryObj)) {
            setGenkaCurrency('');
            setGenkaCurrencyOptions([]);
            setGenkaCurrencyNo('0');
            return false;
        }
        const changed = applyEntry ? applyForcedGenkaJpyIfNeeded(entryObj) : false;
        const layoutCurrencies = genkaCurrencyOptionsFromLayout();
        const entryCurrency = resolveGenkaCurrencyFromEntry(entryObj);
        const effectiveCurrencies = layoutCurrencies.length !== 0 ? layoutCurrencies : (isEmpty(entryCurrency) ? [] : [entryCurrency]);
        setGenkaCurrencyOptions(effectiveCurrencies);
        if (shouldForceGenkaCurrencyJpy(entryObj)) {
            setGenkaCurrency('JPY');
        } else if (effectiveCurrencies.length === 0) {
            setGenkaCurrency(entryCurrency);
        } else if (!isEmpty(entryCurrency) && effectiveCurrencies.indexOf(entryCurrency) !== -1) {
            setGenkaCurrency(entryCurrency);
        } else if (isEmpty(entryCurrency)) {
            setGenkaCurrency(effectiveCurrencies[0]);
        } else {
            setGenkaCurrency('');
        }
        const idx = effectiveCurrencies.indexOf(entryCurrency);
        setGenkaCurrencyNo(idx >= 0 ? String(idx + 1) : '0');
        return changed;
    }
    function syncHinbanGenkaPatternByEntry(entryObj = entrydataRef.current, code = edittingcode) {
        loadHinbanGenkaPattern(code, entryObj);
    }
    function handleHinbanGenkaCurrencyChanged(value) {
        if (!isGenkaCurrencyEditableByLayout()) return;
        const nextValue = shouldForceGenkaCurrencyJpy(entrydataRef.current) ? 'JPY' : String(value ?? '');
        setGenkaCurrency(nextValue);
        setGenkaCurrencyToEntry(entrydataRef.current, nextValue);
        if (shouldForceGenkaCurrencyJpy(entrydataRef.current)) setGenkaRateToEntry(entrydataRef.current, '1');
        syncHinbanGenkaPatternByEntry(entrydataRef.current);
        const genkaPanel = resolveKakousijiGridPanelByType(HINBAN_GENKA_PANEL_TYPE);
        if (!isEmpty(genkaPanel)) recalcHinbanGenkaGridAndSummary(genkaPanel);
        dispatch(entrydata({ ...entrydataRef.current }));
        dispatch(entryrerender());
        fnc.current.modify(true);
    }
    function shouldRefreshHinbanGenkaByColumnId(columnId) {
        const id = String(columnId ?? '').trim().toUpperCase();
        return [
            ...genkaKaigaiKbnColumnIdCandidates,
            ...genkaBrandKbnColumnIdCandidates,
            ...genkaYushoMushoKbnColumnIdCandidates,
            ...genkaShohinKbnColumnIdCandidates,
            ...genkaSupplierCurrencyColumnIdCandidates,
            ...genkaHeaderCurrencyColumnIdCandidates,
            'PDT_JYUTAKUSIRECD'
        ].some((candidate) => String(candidate ?? '').trim().toUpperCase() === id);
    }

    return {
        isHinbanGenkaPanel,
        applyGenkaPatternResult,
        applyGenkaPatternFallback,
        loadHinbanGenkaPattern,
        syncHinbanGenkaCurrencyState,
        syncHinbanGenkaPatternByEntry,
        handleHinbanGenkaCurrencyChanged,
        shouldRefreshHinbanGenkaByColumnId
    };
}
